import { useState } from "react";
import { motion } from "framer-motion";
import {
  Camera,
  Mic2,
  Lightbulb,
  Headphones,
  MonitorPlay,
} from "lucide-react";
import AlaCarteModal from "./AlaCarteModal";
import {
  FloatingOrbs,
  LightLeak,
  CornerMic,
  BroadcastRipple,
  VerticalTextTicker,
  HugeWatermarkText,
} from "./AmbientMotion";

const STUDIO_HERO =
  "https://customer-assets.emergentagent.com/job_cinematic-pods-1/artifacts/6jxtng3s_20250826_152022.jpg";

const FEATURES = [
  { icon: Camera, title: "3 Sony Cameras · 4K", desc: "Cinematic master cuts" },
  { icon: Mic2, title: "RODE PodMics", desc: "Zoom L6 · 32-bit Float" },
  { icon: Lightbulb, title: "TV lighting", desc: "Tunable RGB + key" },
  { icon: Headphones, title: "Pro audio", desc: "Monitored & balanced" },
  { icon: MonitorPlay, title: "1080p Multi-cam LIVE", desc: "Production ready" },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Studio = () => {
  const [alaCarteOpen, setAlaCarteOpen] = useState(false);
  return (
    <section
      id="studio"
      data-testid="studio-section"
      className="relative py-16 md:py-24"
    >
      <FloatingOrbs
        colors={["#00f0ff", "#6b21d9", "#22c55e"]}
        className="opacity-70"
      />
      <VerticalTextTicker
        items={["ON AIR", "STAGE A", "STEREO", "TAKE 01", "LAKELAND FL"]}
        side="left"
        color="#22c55e"
      />
      <HugeWatermarkText text="STUDIO" corner="br" color="#00f0ff" size={260} />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative">
        {/* Section header */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-12 gap-6 items-end mb-14"
        >
          <motion.div
            variants={reveal}
            custom={0}
            className="col-span-12 md:col-span-7"
          >
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#00f0ff] mb-5">
              [ 02 · The Studio ]
            </div>
            <h2
              data-testid="studio-heading"
              className="font-display font-black tracking-tighter text-[44px] md:text-[64px] lg:text-[80px] leading-[0.95]"
            >
              No space. No gear.{" "}
              <span className="italic font-light text-[#a395c3]">
                No problem.
              </span>
              {" "}
              <button
                type="button"
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent("sb:open-booking", {
                      detail: { package: "custom" },
                    }),
                  )
                }
                data-testid="studio-space-rental-cta"
                className="ml-6 md:ml-10 align-middle inline-flex items-center gap-2.5 pl-6 pr-3 py-3 md:py-3.5 rounded-full bg-[#ff0055] text-white font-sans font-semibold text-base md:text-lg tracking-normal hover:bg-white hover:text-[#05050a] transition-colors cursor-pointer touch-manipulation whitespace-nowrap translate-y-[6px] md:translate-y-[8px]"
              >
                Book the Blank Canvas
                <span className="w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-xs">
                  →
                </span>
              </button>
            </h2>
          </motion.div>
          <motion.div
            variants={reveal}
            custom={1}
            className="col-span-12 md:col-span-5 text-[#a395c3] text-base md:text-[17px] leading-relaxed"
          >
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#ff0055] font-semibold mb-3">
              [ The Blank Canvas · <span className="text-white">$90 / hr</span> ]
            </div>
            <p className="text-sm md:text-[15px] text-white/85 leading-relaxed">
              Already have a crew?{" "}
              <span className="text-[#ff0055] font-semibold">
                Rent the room raw — <span className="text-white">$90/hr.</span>
              </span>{" "}
              This is just the space — yours to build, dress, and shoot however
              you imagine.
            </p>
            <p className="mt-3 text-sm md:text-[15px] text-[#a395c3] leading-relaxed">
              Bring your own backdrops, set pieces, props, plants, signage — any
              design you want, we hand you the walls and the floor to make it
              real. Need a piece of our gear? Add it{" "}
              <span className="text-white">à la carte</span>. Includes a{" "}
              <span className="text-white">dedicated hair &amp; makeup
              station</span>{" "}
              and a <span className="text-white">private bathroom</span> for
              wardrobe changes between looks. Two-hour minimum, no setup fees,
              no upsells.
            </p>
            <button
              type="button"
              onClick={() => setAlaCarteOpen(true)}
              data-testid="studio-alacarte-link"
              className="mt-4 inline-flex items-center gap-1.5 text-xs md:text-sm text-[#00f0ff] hover:text-white underline underline-offset-4 decoration-[#00f0ff]/30 hover:decoration-white transition-colors cursor-pointer touch-manipulation font-medium"
            >
              See à la carte gear price sheet
              <span aria-hidden="true">→</span>
            </button>
          </motion.div>
        </motion.div>

        {/* Bento grid */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-12 gap-4 md:gap-5"
        >
          {/* Main studio image */}
          <motion.div
            variants={reveal}
            custom={0}
            className="col-span-12 relative rounded-3xl overflow-hidden border border-white/10 group aspect-[16/10] lg:aspect-auto lg:min-h-[560px]"
            data-testid="studio-main-card"
          >
            <img
              src={STUDIO_HERO}
              alt="SB Media Studio main stage"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
            />
            {/* Warm light-leak in top-right + broadcast mic in top-left */}
            <LightLeak color="#ff8a1e" corner="tr" size={220} />
            <BroadcastRipple color="#00f0ff" corner="tl" size={44} />
            {/* Gradient + caption overlay only on desktop */}
            <div className="hidden lg:block absolute inset-0 bg-gradient-to-tr from-[#05050a] via-[#05050a]/40 to-transparent" />
            <div className="hidden lg:flex relative h-full flex-col justify-end p-10">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#00f0ff]">
                Stage A · Flagship
              </div>
              <div className="font-display font-bold text-3xl md:text-5xl tracking-tight max-w-2xl mt-3">
                Step inside the studio that already earned its spotlight.
              </div>
              <div className="mt-5 text-[#a395c3] text-sm md:text-base max-w-2xl">
                Whether it&apos;s a specific brand shade or a mood-setting vibe,
                our fully tunable RGB lighting, adaptable stage design, and
                multi-color backdrops give you total control. We tailor the
                studio&apos;s environment to fit your vision for podcasts,
                headshots, and branded content.
              </div>
            </div>
          </motion.div>

          {/* Mobile/tablet caption — readable text below the image */}
          <motion.div
            variants={reveal}
            custom={1}
            className="lg:hidden col-span-12 rounded-3xl bg-white/[0.03] border border-white/10 p-6 md:p-8"
            data-testid="studio-main-caption-mobile"
          >
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#00f0ff]">
              Stage A · Flagship
            </div>
            <div className="font-display font-bold text-2xl md:text-4xl tracking-tight mt-3 leading-tight">
              Step inside the studio that already earned its spotlight.
            </div>
            <div className="mt-4 text-[#a395c3] text-sm md:text-base leading-relaxed">
              Whether it&apos;s a specific brand shade or a mood-setting vibe,
              our fully tunable RGB lighting, adaptable stage design, and
              multi-color backdrops give you total control. We tailor the
              studio&apos;s environment to fit your vision for podcasts,
              headshots, and branded content.
            </div>
          </motion.div>
        </motion.div>

        {/* Feature row */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
        >
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              variants={reveal}
              custom={i}
              data-testid={`studio-feature-${i}`}
              className="group rounded-2xl bg-white/[0.025] border border-white/10 p-5 hover:bg-white/[0.05] hover:border-[#00f0ff]/30 transition-all"
            >
              <f.icon
                className="w-5 h-5 text-[#00f0ff] group-hover:scale-110 transition-transform"
                strokeWidth={1.6}
              />
              <div className="font-display font-semibold text-sm mt-4">
                {f.title}
              </div>
              <div className="text-[11px] text-[#a395c3] mt-1">{f.desc}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
      <AlaCarteModal open={alaCarteOpen} onClose={() => setAlaCarteOpen(false)} />
    </section>
  );
};

export default Studio;
